import { reactive, computed } from 'vue'
import { Store } from 'vuex'
import { RouteLocationNormalizedLoaded, Router } from 'vue-router'
import { State as StoreState } from '@/store/index'
import { setAuth } from '@/model/components/users/userAuthinfo'
import { useStoreAction } from '@/model/store/store'
import * as RouterTypeData from '@/model/router/routerType'
import { MENU_TYPE } from '@/model/components/menu/sideMenuType'
import { useRouteUtil } from '@/model/router/route'
import * as log from '@/model/util/log/Logger'
import { DownloadState } from '@/model/components/users/userDownloadState'
import { getFailMsgkey } from '@/model/components/users/userDownloadCode'

/**
 * クエリからダウンロードキーを取得する
 * @param route route
 * @returns ダウンロードキー(未設定の場合は空)
 */
const getQueryKey = (route: RouteLocationNormalizedLoaded) => useRouteUtil(route).getQueryData('key', '')

/**
 * クエリからfail設定値を取得する
 * @param route route
 * @returns failのエラーコード(未設定の場合は空)
 */
const getQueryFail = (route: RouteLocationNormalizedLoaded) => useRouteUtil(route).getQueryData('fail', '')

/**
 * 画面読み込み時の失敗コードを返す
 * @param fail fail設定値
 * @returns リソースキー(未設定の場合は空)
 */
const getFailmsg = (fail: string): string => {
  // クエリに設定されている場合はコードに紐づくリソースキーを取得
  const resourceKey = getFailMsgkey(fail)
  if (resourceKey) {
    return resourceKey.key
  }
  // 何も設定されていない場合は空を返す
  return ''
}

/**
 * 画面読み込み時のダウンロードキーを返す
 * @param props プロパティ情報
 * @param route route
 * @returns
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getData = (props: any, route: RouteLocationNormalizedLoaded) => {
  const querykey = getQueryKey(route)
  const queryfail = getQueryFail(route)
  const { id } = props
  const { searchkey } = props
  log.debug(`Dowload First. searchkey:${searchkey}, id:${id}, querykey:${querykey}, queryfail:${queryfail}`)
  // ログインから引き継いだデータがある場合はそれを返す
  if (searchkey) {
    return {
      searchkey: searchkey as string,
      failSearchKey: '',
      failDetailKey: '',
    }
  }
  // failに紐づくリソースキーを取得
  const failmsg = getFailmsg(queryfail)
  // urlの/users/download/:idのURLの場合はidはダウンロードキーなので返し、failが設定されている場合は検索するのでdetailに設定
  if (id) {
    return {
      searchkey: id as string,
      failSearchKey: '',
      failDetailKey: failmsg,
    }
  }
  // /users/download?key=12345等でquerykeyが設定されている場合はquerykeyを返し、failが設定されている場合は検索するのでdetailに設定
  if (querykey) {
    return {
      searchkey: querykey,
      failSearchKey: '',
      failDetailKey: failmsg,
    }
  }
  // 何も設定されていない場合は空を返し、failが設定されている場合は検索しないのでdetailに設定
  return {
    searchkey: '',
    failSearchKey: failmsg,
    failDetailKey: '',
  }
}

/**
 * 画面の初期状態を取得する
 * @param props propsデータ
 * @returns reactiveした初期状態、子コンポーネント表示有無
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useDownload = (props: any, route: RouteLocationNormalizedLoaded) => {
  const data = getData(props, route)
  const state = reactive<DownloadState>({
    init: false,
    key: data.searchkey,
    isSearch: props.isSearch === 'true',
    failSearchKey: data.failSearchKey,
    failDetailKey: data.failDetailKey,
  })
  // 子コンポーネント表示は初期検索しない場合または、初期検索する場合でinitDomの初期化が終了した時にtrueになる
  const isChild = computed(() => !state.key || state.init)
  return { state, isChild }
}

/**
 * ダウンロード画面のDOM初期処理を実施する
 * @param searchkey 状態
 * @param store vuex
 * @param router vue router
 */
const initDom = async (state: DownloadState, store: Store<StoreState>, router: Router) => {
  // 検索して初期化以外の場合は処理処理を実施
  if (state.isSearch) {
    // 一旦ログイン済みにする
    await useStoreAction(store).login()
    // ユーザー認証情報を設定(認証済みの場合は何もしない)
    const data: RouterTypeData.LoginTopData = {
      redirect: RouterTypeData.REDIRECT_TYPE.DOWNLOAD,
      downloadKey: state.key,
    }
    // 認証情報の設定結果を確認
    const authResult = await setAuth(store, router, data)
    log.debug(`Download First. authResult:${authResult}, key:${state.key}`)
    // 認証が成功した場合のみ後続の処理を実行
    if (authResult) {
      // 詳細情報をクリア
      await useStoreAction(store).downloadDetailInit()
      // メニュー情報を設定
      await useStoreAction(store).menu(MENU_TYPE.DOWNLOAD)
      // 初期化済みを設定
      state.init = true
    }
    // 認証失敗の場合は何もしない（TOP画面へのリダイレクトは setAuth 内で行われる）
    return
  }
  // 検索しない場合は初期化済みを設定
  state.init = true
}

export { useDownload, initDom }
